import React, { useState, useEffect, useContext, useRef } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import SearchIcon from '@material-ui/icons/Search';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { AppContext } from 'app/containers/Main';
import { invalidNPIForCreateProvider } from 'hooks/useMainDataManager/useMainDataManager';
import getProviderDisplayNames from 'utils/providerUtils';

import Footer from '../Footer/Footer';
import ProviderPill from '../ProviderPill/ProviderPill';
import { useStyles } from '../styles';
import { ProviderState } from '../types';

const STORE_KEY = 'selectedState';

const stateModel = {
  id: -1,
  abbreviation: '',
  country_id: 0,
  name: 'Choose State',
};

const ProviderLookup = () => {
  const classes = useStyles();

  const {
    loading,
    options,
    states,
    setSearchRequest,
    setStateRequest,
    setConfirmedProvider,
    setStep,
  }: any = useContext(AppContext);

  const [selectedState, setSelectedState] = useState(stateModel.abbreviation);
  const [usaStates, setUsaStates] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [timeOutVar, setTimeOutVar] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState<ProviderState | null>(null);
  const [providerConfirmed, setProviderConfirmed] = useState(false);

  const autoselectRef = useRef(null);

  useEffect(() => {
    if(!!options.length) {
      setOpen(true);
    }
  }, [options]);

  useEffect(() => {
    if (timeOutVar) {
      clearTimeout(timeOutVar);
      setTimeOutVar(0);
    }

    if (!searchValue) {
      return undefined;
    }

    let timeOutVarTmp = setTimeout(() => {
      setSearchRequest(searchValue, selectedState);
    }, 500);

    // @ts-ignore
    setTimeOutVar(timeOutVarTmp);
  }, [searchValue]);

  useEffect(() => {
    if (providerConfirmed) {
      setConfirmedProvider(selectedProvider);
      setStep(2);
    }
  }, [providerConfirmed]);

  useEffect(() => {
    if (!selectedProvider) {
      // @ts-ignore
      // TODO find if there's a better way to do this
      autoselectRef?.current?.children[0]?.children[0]?.focus();
    } else {
      if (selectedProvider.npi_id === invalidNPIForCreateProvider) {
        setConfirmedProvider(selectedProvider);
        setStep(2);
      }
    }
  }, [selectedProvider]);

  // load the list of states
  useEffect(() => {
    setStateRequest();
  }, []);

  useEffect(() => {
    const formattedStates = states.map((x: any) => {
      return {
        abbreviation: x.abbreviation,
        country_id: x.country_id,
        id: x.id,
        name: `${x.name} - ${x.abbreviation}`
      };
    });
    setUsaStates(formattedStates.sort((a,b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)));
  }, [states]);

  const stateHandler = (event: any, value: any) => {
    var selectedState = !!value ? value.abbreviation : stateModel.abbreviation;
    setSelectedState(selectedState);
  };

  const updateSelectedProvider = (event, value) => {
    setSelectedProvider(value);
  };

  const updateSearch = e => {
    setSearchValue(e.target.value);
  };

  // OLD FILTER METHOD USING THE FACTORY PROVIDED BY MATERIAL UI
  // const filterOptions = createFilterOptions({
  //   matchFrom: 'any',
  //   stringify: (option: any) => {
  //     console.log(option);
  //     return `${option.provider_name} ${option.npi_id}`
  //   },
  // });

  // CUSTOM FILTER METHOD
  const filterOptions = ((options, {inputValue}) => {
    const filteredOptions = options.filter(option => {
      return option.provider_name.toLowerCase().includes(inputValue.toLowerCase()) || (option.npi_id !== null && option.npi_id.toString().includes(inputValue)) || option.npi_id === invalidNPIForCreateProvider;
    });
    return filteredOptions;
  });


  return (
    <>
      <p className={classes.text}>
        We depend on our community of physicians like you to maintain our online directory's quality and accuracy. To
        start, we’re going to ask you to look up your profile. Once you’ve found it, you’ll be able to create a secure
        account and make updates.
      </p>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4} lg={4}>
          <div className={classes.labelForInput}>State</div>
          <Autocomplete
            id="state-autocomplete"
            options={usaStates}
            getOptionLabel={(option: any) => option.name}
            onChange={stateHandler}
            autoSelect
            autoHighlight
            fullWidth
            renderInput={params => <TextField {...params} placeholder="State" variant="outlined" />}
          />
        </Grid>

        <Grid item xs={12} sm={8} lg={8}>
          <div className={classes.labelForInput}>Name or NPI</div>
          <Autocomplete
            id="provider-input"
            fullWidth
            open={open}
            onOpen={() => {
              if(searchValue) {
                setOpen(true);
              }
            }}
            onClose={() => {
              setOpen(false);
            }}
            getOptionSelected={(option, value) => option.provider_id === value.provider_id}
            getOptionLabel={option => `${option.provider_name}`}
            filterOptions={filterOptions}
            onChange={updateSelectedProvider}
            options={options}
            loading={loading}
            onInputChange={updateSearch}
            renderInput={params => (
              <TextField
                {...params}
                placeholder="Name or NPI"
                variant="outlined"
                ref={autoselectRef}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            renderOption={option => {
              const lines = getProviderDisplayNames(option);
              if (option.npi_id === invalidNPIForCreateProvider) {
                return (
                  <div>
                    <p>I can't find my profile.</p>
                  </div>
                );
              } else {
                return (
                  <div className={classes.locationOption}>
                    {option.real_photo_path ? (
                      <img src={option.real_photo_path} alt="provider" />
                    ) : (
                      <div className={classes.providerPreviewImgSelect}>
                        <svg>
                          <use
                            xlinkHref={
                              option.gender && option.gender === 'F' ? '#icon-provider-female' : '#icon-provider-male'
                            }
                          />
                        </svg>
                      </div>
                    )}
                    <div className={classes.providerContainer}>
                      <span className={classes.providerName} title={lines[0]}>
                        {lines[0]}
                      </span>
                      <span className={classes.providerAddress} title={lines[1]}>
                        {lines[1]}
                      </span>
                      <span className={classes.providerAddress} title={lines[2]}>
                        {lines[2]}
                      </span>
                    </div>
                  </div>
                );
              }
            }}
          />
        </Grid>
        {selectedProvider && selectedProvider.npi_id !== invalidNPIForCreateProvider ? (
          <ProviderPill
            provider={selectedProvider}
            setProviderConfirmed={setProviderConfirmed}
            setSelectedProvider={setSelectedProvider}
          />
        ) : (
          <Grid item xs={12}>
            <div className={classes.searchBox}>
              <SearchIcon />
              Use the search field above to find your profile.
            </div>
          </Grid>
        )}
      </Grid>
      <Footer />
    </>
  );
};

export default ProviderLookup;
